import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { API } from "@/lib/api";
import { Send } from "lucide-react";

const SUGGESTIONS = [
  "How do I discern God's will in a hard decision?",
  "Help me quiet my anxious heart tonight.",
  "What does it mean to walk in step with the Spirit?",
  "Give me a verse to meditate on for peace.",
];

const FALLBACK_REPLY = "I'm having trouble responding right now. Please try again.";

function newId(prefix) {
  return crypto.randomUUID ? crypto.randomUUID() : `${prefix}-${Date.now()}`;
}

async function streamGuideReply(sessionId, content, onDelta) {
  const res = await fetch(`${API}/guide/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId, message: content }),
  });
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop();
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      try {
        const { c } = JSON.parse(payload);
        if (c) onDelta(c);
      } catch {
        // skip malformed chunk
      }
    }
  }
}

function Dot() {
  return <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-pulse" style={{ animationDelay: `${Math.random() * 0.6}s` }} />;
}

function GuideMessage({ m }) {
  return (
    <div className="animate-fade-up" data-testid={`message-${m.role}`}>
      <p className={`label-overline mb-3 ${m.role === "assistant" ? "text-accent" : ""}`}>
        {m.role === "assistant" ? "The Guide" : "You"}
      </p>
      {m.role === "assistant" ? (
        <div className="border-l-2 border-primary/20 pl-6 font-body text-lg leading-relaxed whitespace-pre-wrap text-foreground">
          {m.text || <span className="inline-flex gap-1 items-center text-muted-foreground"><Dot /><Dot /><Dot /></span>}
        </div>
      ) : (
        <p className="text-lg leading-relaxed whitespace-pre-wrap text-foreground/90">{m.text}</p>
      )}
    </div>
  );
}

function EmptyState({ onPick }) {
  return (
    <div className="animate-fade-up">
      <p className="font-scripture italic text-2xl sm:text-3xl text-primary leading-relaxed max-w-xl">
        Peace be with you. I am here to walk alongside you in Scripture and prayer. What is on your heart?
      </p>
      <div className="grid sm:grid-cols-2 gap-3 mt-10">
        {SUGGESTIONS.map((s) => (
          <button key={s} data-testid="guide-suggestion" onClick={() => onPick(s)}
            className="text-left border border-border rounded-sm px-5 py-4 text-sm hover:bg-muted/50 transition-colors duration-300">
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Guide() {
  const [sessionId] = useState(() => newId("s"));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const prefill = searchParams.get("prefill");
    if (prefill) {
      setInput(prefill);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const replaceLastMessage = (updater) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = updater(copy[copy.length - 1]);
      return copy;
    });

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m,
      { id: newId("u"), role: "user", text: content },
      { id: newId("a"), role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      await streamGuideReply(sessionId, content, (c) =>
        replaceLastMessage((last) => ({ ...last, text: last.text + c })));
    } catch {
      replaceLastMessage((last) => ({ ...last, text: FALLBACK_REPLY }));
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex flex-col h-screen max-h-screen">
      <div className="border-b border-border px-6 md:px-12 py-6 bg-card/40">
        <p className="label-overline">Spiritual Companion</p>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight leading-none mt-1">The Guide</h1>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 md:px-12">
        <div className="max-w-3xl mx-auto py-10">
          {messages.length === 0 ? (
            <EmptyState onPick={send} />
          ) : (
            <div className="space-y-10">
              {messages.map((m) => <GuideMessage key={m.id} m={m} />)}
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-border bg-card/40 px-6 md:px-12 py-5">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="max-w-3xl mx-auto flex items-end gap-3">
          <textarea data-testid="guide-input" value={input} onChange={(e) => setInput(e.target.value)} rows={1}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Share what is on your heart…"
            className="flex-1 resize-none bg-transparent border-b border-border py-2 max-h-32 focus:outline-none focus:border-primary transition-colors leading-relaxed" />
          <button data-testid="guide-send-button" type="submit" disabled={streaming || !input.trim()}
            className="w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-40 shrink-0">
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
